/**
 * mcpFeatures.js — public marketing/landing subpage for Heist's MCP integration.
 *
 * Mounted at /#mcp-features (public, no auth required). Linked from the
 * LoginPage and SignupPage hero CTAs. Designed to sell the MCP value
 * proposition deeply: the URL-paste OAuth flow (no API keys), MCP_TOOL_COUNT tools
 * grouped into categories, a richer prompts library than the help center
 * has room for, "tips for talking to Heist" best practices, FAQ, and a
 * trial CTA at the bottom.
 *
 * Phase 7 follow-up. Authored after user feedback that the auth hero +
 * help-center MCP tab were too dense and pushed users toward API keys
 * (the advanced path) instead of the OAuth URL paste (the recommended
 * path). This page collects the deep MCP marketing in one place so the
 * auth pages can stay short.
 */

// PROMPTS, TOOL_GROUPS (TOOL_GROUPS), TIPS, and FAQ all come from the
// shared data module so this marketing page and the in-app help-center
// MCP tab (stubs.js) render the same content. Update once.
import {
    PROMPTS,
    MCP_PUBLIC_URL,
    MCP_TOOL_COUNT as TOOL_COUNT,
    MCP_TOOL_GROUPS as TOOL_GROUPS,
    MCP_TIPS as TIPS,
    MCP_FAQ as FAQ,
} from '../data/mcpPrompts.js';

// Local alias for readability in the template — MCP_PUBLIC_URL is the
// canonical export. Single source of truth lives in mcpPrompts.js.
const MCP_URL = MCP_PUBLIC_URL;
const PROMPT_COUNT = 9;

export class McpFeaturesPage {
    constructor() {
        this._copyTimer = null;
    }

    async mount(el) {
        this.container = el;
        el.innerHTML = this._render();
        this._bindEvents();
    }

    _render() {
        return `
        <div class="mcpf-page">
          <!-- Top bar with logo + login link -->
          <header class="mcpf-topbar">
            <a href="#login" class="mcpf-logo" aria-label="Back to Heist">
              <img class="mcpf-logo__mark" src="/img/heist-mark.svg" alt="" />
              <span class="mcpf-logo__word">Heist<span>.</span></span>
            </a>
            <nav class="mcpf-topnav">
              <a href="#mcp-features-prompts">Prompts</a>
              <a href="#mcp-features-tools">Tools</a>
              <a href="#mcp-features-tips">Tips</a>
              <a href="#mcp-features-faq">FAQ</a>
              <a href="#login" class="mcpf-topnav-login">Log in</a>
              <a href="#signup" class="btn btn-primary btn-sm">Start free trial</a>
            </nav>
          </header>

          <!-- Hero -->
          <section class="mcpf-hero">
            <div class="mcpf-hero__eyebrow">For Claude.ai &middot; ChatGPT &middot; Cursor &middot; Windsurf</div>
            <h1 class="mcpf-hero__headline">Run your entire content operation from your AI chat.</h1>
            <p class="mcpf-hero__sub">${TOOL_COUNT} tools. Brand voice, scheduling, blogs, audit, revert — all of Heist, accessible by talking. Like you already talk to Claude. No code. No config files. About 30 seconds to set up.</p>

            <div class="mcpf-url-card">
              <div class="mcpf-url-card__label">Paste this URL into your AI chat's Connectors</div>
              <div class="mcpf-url-card__row">
                <code class="mcpf-url" data-mcpf-url>${MCP_URL}</code>
                <button class="btn btn-primary btn-sm" data-action="copy-url">Copy URL</button>
              </div>
              <div class="mcpf-url-card__hint">In Claude.ai or ChatGPT: <strong>Settings → Connectors → Add custom connector</strong>. Paste, click Add, approve. Done.</div>
            </div>

            <div class="mcpf-hero__cta">
              <a href="#signup" class="btn btn-primary btn-lg">Start 7-day free trial</a>
              <a href="#login" class="btn btn-secondary btn-lg">I already have an account</a>
            </div>
            <div class="mcpf-hero__sub-cta">Pro plan required for MCP. Trial includes everything — no credit card.</div>
          </section>

          <!-- "Why this matters" — three short value props -->
          <section class="mcpf-why">
            <div class="mcpf-why__grid">
              <div class="mcpf-why__card">
                <div class="mcpf-why__icon"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg></div>
                <h3>One conversation = a month of content</h3>
                <p>Plan campaigns, generate drafts, schedule posts, edit blogs, all in a chat. Heist's tools collapse what used to take 8 separate apps into one window.</p>
              </div>
              <div class="mcpf-why__card">
                <div class="mcpf-why__icon"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96.44 2.5 2.5 0 0 1-2.96-3.08 3 3 0 0 1-.34-5.58 2.5 2.5 0 0 1 1.32-4.24 2.5 2.5 0 0 1 4.44-2.04z"/><path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96.44 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24 2.5 2.5 0 0 0-4.44-2.04z"/></svg></div>
                <h3>Your Brand Brain travels with you</h3>
                <p>Voice, rules, audience, offers, top-performers — all loaded into every conversation. The AI doesn't need re-prompting. It already knows you.</p>
              </div>
              <div class="mcpf-why__card">
                <div class="mcpf-why__icon"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 14 4 9 9 4"/><path d="M20 20v-7a4 4 0 0 0-4-4H4"/></svg></div>
                <h3>Mistakes are reversible</h3>
                <p>Every write stamps an audit row. "Revert that bulk delete I just did" actually works — Heist restores the before-state. Most ops are undo-able.</p>
              </div>
            </div>
          </section>

          <!-- Prompts library — the meat of the page -->
          <section class="mcpf-section" id="mcp-features-prompts">
            <div class="mcpf-section__head">
              <div class="mcpf-section__eyebrow">Examples</div>
              <h2 class="mcpf-section__title">Example prompts to start — or just talk like you would to Claude</h2>
              <p class="mcpf-section__sub">These aren't required. The connector works like any normal Claude or ChatGPT chat — ask in your own words. The ${TOOL_COUNT} tools fire automatically based on what you're asking for. Steal these prompts, paraphrase them, or ignore them and just talk.</p>
            </div>

            <!-- Flexibility callout — frames the prompts as starters, not requirements -->
            <div class="mcpf-flex-callout">
              <div class="mcpf-flex-callout__title">You don't have to use these.</div>
              <div class="mcpf-flex-callout__body">
                The Heist connector is just an extension of your AI chat. Ask questions, change your mind mid-thread, follow up — the same way you'd talk to Claude without Heist. Heist's ${TOOL_COUNT} tools run quietly behind the conversation. Below are organized examples to spark ideas, not a script you have to follow.
              </div>
            </div>

            ${PROMPTS.map(group => `
              <div class="mcpf-prompt-group">
                <h3 class="mcpf-prompt-group__title">${group.category}</h3>
                <div class="mcpf-prompt-grid">
                  ${group.items.map(item => `
                    <div class="mcpf-prompt-card">
                      <div class="mcpf-prompt-card__title">${item.title}</div>
                      <div class="mcpf-prompt-card__prompt">
                        <code>${this._esc(item.prompt)}</code>
                        <button class="mcpf-prompt-card__copy" data-action="copy-prompt" data-prompt="${this._escAttr(item.prompt)}" aria-label="Copy prompt">Copy</button>
                      </div>
                      <div class="mcpf-prompt-card__why">${item.why}</div>
                    </div>
                  `).join('')}
                </div>
              </div>
            `).join('')}
          </section>

          <!-- Tool inventory — grouped, not the full 85 -->
          <section class="mcpf-section mcpf-section--alt" id="mcp-features-tools">
            <div class="mcpf-section__head">
              <div class="mcpf-section__eyebrow">What you get</div>
              <h2 class="mcpf-section__title">${TOOL_COUNT} tools, grouped by what you actually do</h2>
              <p class="mcpf-section__sub">Your AI chat picks the right tool automatically. You ask in plain English; Heist routes the call. The full catalog is listed at runtime via <code>tools/list</code> — your chat client surfaces them.</p>
            </div>
            <div class="mcpf-tool-grid">
              ${TOOL_GROUPS.map(g => `
                <div class="mcpf-tool-card">
                  <div class="mcpf-tool-card__head">
                    <div class="mcpf-tool-card__name">${g.name}</div>
                    <div class="mcpf-tool-card__count">${g.count}</div>
                  </div>
                  <div class="mcpf-tool-card__desc">${g.desc}</div>
                </div>
              `).join('')}
            </div>
          </section>

          <!-- Tips for talking to Heist -->
          <section class="mcpf-section" id="mcp-features-tips">
            <div class="mcpf-section__head">
              <div class="mcpf-section__eyebrow">Best practices</div>
              <h2 class="mcpf-section__title">Tips for talking to Heist</h2>
              <p class="mcpf-section__sub">Short habits that make the agent useful instead of frustrating. Most are obvious in hindsight; not all are obvious up front.</p>
            </div>
            <div class="mcpf-tips">
              ${TIPS.map((t, i) => `
                <div class="mcpf-tip">
                  <div class="mcpf-tip__num">${String(i + 1).padStart(2, '0')}</div>
                  <div>
                    <h4>${t.title}</h4>
                    <p>${t.body}</p>
                  </div>
                </div>
              `).join('')}
            </div>
          </section>

          <!-- FAQ -->
          <section class="mcpf-section mcpf-section--alt" id="mcp-features-faq">
            <div class="mcpf-section__head">
              <div class="mcpf-section__eyebrow">FAQ</div>
              <h2 class="mcpf-section__title">Common questions</h2>
            </div>
            <div class="mcpf-faq">
              ${FAQ.map((f, i) => `
                <details class="mcpf-faq-item"${i === 0 ? ' open' : ''}>
                  <summary class="mcpf-faq-q">${f.q}</summary>
                  <div class="mcpf-faq-a">${f.a}</div>
                </details>
              `).join('')}
            </div>
          </section>

          <!-- Final CTA -->
          <section class="mcpf-final-cta">
            <h2>Ready to talk your way to a content calendar?</h2>
            <p>7-day free trial. No card. Pro plan unlocks the MCP connector after that.</p>
            <div class="mcpf-final-cta__actions">
              <a href="#signup" class="btn btn-primary btn-lg">Start free trial</a>
              <a href="#login" class="btn btn-secondary btn-lg">Log in</a>
            </div>
          </section>

          <footer class="mcpf-footer">
            <a href="#login" class="mcpf-footer__logo">
              <img class="mcpf-logo__mark" src="/img/heist-mark.svg" alt="" />
              <span class="mcpf-logo__word">Heist<span>.</span></span>
            </a>
            <div class="mcpf-footer__tagline">The brains behind the operation.</div>
          </footer>
        </div>
        `;
    }

    _esc(s) {
        return String(s || '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;');
    }

    _escAttr(s) {
        return this._esc(s).replace(/"/g, '&quot;');
    }

    _bindEvents() {
        this.container.addEventListener('click', (e) => {
            const btn = e.target.closest('[data-action]');
            if (!btn) return;
            const action = btn.dataset.action;

            if (action === 'copy-url') {
                this._copy(MCP_URL, btn);
            }
            if (action === 'copy-prompt') {
                const text = btn.dataset.prompt || '';
                this._copy(text, btn);
            }
        });
    }

    _copy(text, btn) {
        const original = btn.textContent;
        const restore = () => { btn.textContent = original; };
        const flash = (label) => {
            btn.textContent = label;
            if (this._copyTimer) clearTimeout(this._copyTimer);
            this._copyTimer = setTimeout(restore, 1500);
        };
        try {
            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(text).then(
                    () => flash('✓ Copied'),
                    () => flash('Copy failed')
                );
                return;
            }
        } catch (_) { /* fall through */ }
        // Legacy fallback
        try {
            const ta = document.createElement('textarea');
            ta.value = text;
            ta.style.position = 'fixed';
            ta.style.left = '-9999px';
            document.body.appendChild(ta);
            ta.select();
            document.execCommand('copy');
            document.body.removeChild(ta);
            flash('✓ Copied');
        } catch (_) {
            flash('Copy failed');
        }
    }
}
